import dayjs, { Dayjs } from 'dayjs';
import meanBy from 'lodash/meanBy';

import { SECONDS_IN_DAY } from '@cast/constants';
import type { DateTime } from '@cast/shared-types';
import { CostReport } from '@cast/types';
import { dateRangeIterator } from '@cast/utils';

import { HourlyClusterCostReportData } from 'features/cost-report/types/costOverTime';
import { DataGranularity } from 'types/cost-report';
import { granularityToSeconds } from 'utils/costReport';

import { emptyDataPoint } from '../utils';

function mapToClusterCostReport(
  data: CostReport[]
): HourlyClusterCostReportData[] {
  return data.map(
    ({
      timestamp,
      costSpot: spotCost,
      costOnDemand: onDemandCost,
      costSpotFallback: fallbackCost,
      cpuCostOnDemand: onDemandCpuCost,
      cpuCostSpot: spotCpuCost,
      cpuCostSpotFallback: fallbackCpuCost,
      cpuCountOnDemand: onDemandCpuCount,
      cpuCountSpot: spotCpuCount,
      cpuCountSpotFallback: fallbackCpuCount,
      ramCostOnDemand: onDemandRamCost,
      ramCostSpot: spotRamCost,
      ramCostSpotFallback: fallbackRamCost,
      ramGibOnDemand: onDemandRamGib,
      ramGibSpot: spotRamGib,
      ramGibSpotFallback: fallbackRamGib,
      gpuCostOnDemand: onDemandGpuCost,
      gpuCostSpot: spotGpuCost,
      gpuCostSpotFallback: fallbackGpuCost,
      gpuCountOnDemand: onDemandGpuCount,
      gpuCountSpot: spotGpuCount,
      gpuCountSpotFallback: fallbackGpuCount,
      tpuCostOnDemand: onDemandTpuCost = 0,
      tpuCostSpot: spotTpuCost = 0,
      tpuCostSpotFallback: fallbackTpuCost = 0,
      tpuCountOnDemand: onDemandTpuCount = 0,
      tpuCountSpot: spotTpuCount = 0,
      tpuCountSpotFallback: fallbackTpuCount = 0,
      storageCost,
      storageCount,
    }) => {
      const spotCostPerCpu = spotCpuCount ? spotCpuCost / spotCpuCount : 0;
      const spotCostPerGpu = spotGpuCount ? spotGpuCost / spotGpuCount : 0;
      const spotCostPerTpu = spotTpuCount ? spotTpuCost / spotTpuCount : 0;

      const totalCpu = onDemandCpuCount + fallbackCpuCount + spotCpuCount;
      const totalGpu = onDemandGpuCount + fallbackGpuCount + spotGpuCount;
      const totalTpu = onDemandTpuCount + fallbackTpuCount + spotTpuCount;
      const totalRamGib = onDemandRamGib + fallbackRamGib + spotRamGib;
      const totalCost = onDemandCost + fallbackCost + spotCost + storageCost;
      const totalCpuCost = onDemandCpuCost + fallbackCpuCost + spotCpuCost;
      const totalGpuCost = onDemandGpuCost + fallbackGpuCost + spotGpuCost;
      const totalTpuCost = onDemandTpuCost + fallbackTpuCost + spotTpuCost;
      const totalRamCost = onDemandRamCost + fallbackRamCost + spotRamCost;

      return {
        timestamp,

        // General cluster metrics
        totalCost,
        onDemandCost,
        fallbackCost,
        spotCost,

        // CPU metrics
        totalCpu,
        totalCpuCost,
        onDemandCpuCost,
        spotCpuCost,
        fallbackCpuCost,
        onDemandCpuCount,
        spotCpuCount,
        fallbackCpuCount,
        onDemandCostPerCpu: onDemandCpuCount
          ? onDemandCpuCost / onDemandCpuCount
          : 0,
        spotCostPerCpu,
        fallbackCostPerCpu: fallbackCpuCount
          ? fallbackCpuCost / fallbackCpuCount
          : 0,
        normalizedCostPerCpu: totalCpu ? totalCpuCost / totalCpu : 0,

        // RAM metrics
        totalRamGib,
        totalRamCost,
        onDemandRamCost,
        spotRamCost,
        fallbackRamCost,
        onDemandRamGib,
        spotRamGib,
        fallbackRamGib,
        onDemandCostPerGib: onDemandRamGib
          ? onDemandRamCost / onDemandRamGib
          : 0,
        spotCostPerGib: spotRamGib ? spotRamCost / spotRamGib : 0,
        fallbackCostPerGib: fallbackRamGib
          ? fallbackRamCost / fallbackRamGib
          : 0,
        normalizedCostPerGib: totalRamGib ? totalRamCost / totalRamGib : 0,

        // GPU metrics
        totalGpu,
        totalGpuCost,
        onDemandGpuCost,
        spotGpuCost,
        fallbackGpuCost,
        onDemandGpuCount,
        spotGpuCount,
        fallbackGpuCount,
        onDemandCostPerGpu: onDemandGpuCount
          ? onDemandGpuCost / onDemandGpuCount
          : 0,
        spotCostPerGpu,
        fallbackCostPerGpu: fallbackGpuCount
          ? fallbackGpuCost / fallbackGpuCount
          : 0,
        normalizedCostPerGpu: totalGpu ? totalGpuCost / totalGpu : 0,

        // TPU metrics
        totalTpu,
        totalTpuCost,
        onDemandTpuCost,
        spotTpuCost,
        fallbackTpuCost,
        onDemandTpuCount,
        spotTpuCount,
        fallbackTpuCount,
        onDemandCostPerTpu: onDemandTpuCount
          ? onDemandTpuCost / onDemandTpuCount
          : 0,
        spotCostPerTpu,
        fallbackCostPerTpu: fallbackTpuCount
          ? fallbackTpuCost / fallbackTpuCount
          : 0,
        normalizedCostPerTpu: totalTpu ? totalTpuCost / totalTpu : 0,

        // Storage metrics
        totalStorage: storageCount,
        totalStorageCost: storageCost,
        // Storage doesn't have resource offerings, normalizedCostPerStorage is the same as costPerStorage
        normalizedCostPerStorage: storageCount ? storageCost / storageCount : 0,
        costPerStorage: storageCount ? storageCost / storageCount : 0,

        // App related metrics
        forecast: false,
        onDemandUptime: 0,
        fallbackUptime: 0,
        spotUptime: 0,
      };
    }
  );
}

type ApplyMovingAverageParams = {
  data: HourlyClusterCostReportData[];
  endTime: Dayjs;
  granularity: DataGranularity;
  windowSize: number;
};

function applyMovingAverage({
  data,
  endTime,
  granularity,
  windowSize,
}: ApplyMovingAverageParams): HourlyClusterCostReportData[] {
  const now = dayjs.utc();
  // it's easier to start from the latest available timestamp and skip data points in the past
  // than calculate the correct start time for granularity
  const fromTime = dayjs
    .utc(data[data.length - 1].timestamp)
    .add(granularity.step, granularity.unit);

  // This handles BE/FE timestamp mismatch: BE uses period start (00:00.000), FE uses period end (59:59.999).
  if (fromTime.isSameOrAfter(endTime)) {
    return data;
  }

  let toTime = endTime // required to make dateRangeIterator work not inclusive
    .subtract(granularity.step, granularity.unit);

  // range selection in the app (e.g. chart, picker) gives 59:59.999 time - align to match the data timestamps.
  if (toTime.millisecond() === 999) {
    toTime = toTime.add(1, 'ms');
  }

  const result: HourlyClusterCostReportData[] = [...data];

  for (const timestamp of dateRangeIterator(fromTime, toTime, granularity)) {
    if (timestamp.isSameOrBefore(now)) {
      continue;
    }

    const window = result.slice(-windowSize);

    const onDemandCost = meanBy(window, 'onDemandCost') || 0;
    const onDemandCpuCost = meanBy(window, 'onDemandCpuCost') || 0;
    const onDemandRamCost = meanBy(window, 'onDemandRamCost') || 0;
    const onDemandGpuCost = meanBy(window, 'onDemandGpuCost') || 0;
    const onDemandCpuCount = meanBy(window, 'onDemandCpuCount') || 0;
    const onDemandRamGib = meanBy(window, 'onDemandRamGib') || 0;
    const onDemandGpuCount = meanBy(window, 'onDemandGpuCount') || 0;
    const onDemandUptime = meanBy(window, 'onDemandUptime') || 0;

    const fallbackCost = meanBy(window, 'fallbackCost') || 0;
    const fallbackCpuCost = meanBy(window, 'fallbackCpuCost') || 0;
    const fallbackGpuCost = meanBy(window, 'fallbackGpuCost') || 0;
    const fallbackRamCost = meanBy(window, 'fallbackRamCost') || 0;
    const fallbackCpuCount = meanBy(window, 'fallbackCpuCount') || 0;
    const fallbackRamGib = meanBy(window, 'fallbackRamGib') || 0;
    const fallbackGpuCount = meanBy(window, 'fallbackGpuCount') || 0;
    const fallbackUptime = meanBy(window, 'fallbackUptime') || 0;

    const spotCost = meanBy(window, 'spotCost') || 0;
    const spotCpuCount = meanBy(window, 'spotCpuCount') || 0;
    const spotRamGib = meanBy(window, 'spotRamGib') || 0;
    const spotGpuCount = meanBy(window, 'spotGpuCount') || 0;
    const spotCpuCost = meanBy(window, 'spotCpuCost') || 0;
    const spotGpuCost = meanBy(window, 'spotGpuCost') || 0;
    const spotRamCost = meanBy(window, 'spotRamCost') || 0;
    const spotUptime = meanBy(window, 'spotUptime') || 0;

    const totalCpu = onDemandCpuCount + fallbackCpuCount + spotCpuCount;
    const totalRamGib = onDemandRamGib + fallbackRamGib + spotRamGib;
    const totalGpu = onDemandGpuCount + fallbackGpuCount + spotGpuCount;

    const totalStorage = meanBy(window, 'totalStorage') || 0;
    const totalStorageCost = meanBy(window, 'totalStorageCost') || 0;

    const totalCost = onDemandCost + fallbackCost + spotCost;
    const totalCpuCost = onDemandCpuCost + fallbackCpuCost + spotCpuCost;
    const totalRamCost = onDemandRamCost + fallbackRamCost + spotRamCost;
    const totalGpuCost = onDemandGpuCost + fallbackGpuCost + spotGpuCost;

    const normalizedCostPerCpu = totalCpu ? totalCpuCost / totalCpu : 0;
    const normalizedCostPerGib = totalRamGib ? totalRamCost / totalRamGib : 0;
    const normalizedCostPerGpu = totalGpu ? totalGpuCost / totalGpu : 0;
    const costPerStorage = totalStorage ? totalStorageCost / totalStorage : 0;

    result.push({
      ...emptyDataPoint,
      forecast: true,
      timestamp: timestamp.toISOString(),

      totalCpu,
      totalRamGib,
      totalGpu,
      totalCost,
      totalCpuCost,
      totalRamCost,
      totalGpuCost,
      totalStorage,
      totalStorageCost,
      normalizedCostPerCpu,
      normalizedCostPerGib,
      normalizedCostPerGpu,
      costPerStorage,

      onDemandCost,
      onDemandCostPerCpu: onDemandCpuCount
        ? onDemandCpuCost / onDemandCpuCount
        : 0,
      onDemandCostPerGpu: onDemandGpuCount
        ? onDemandGpuCost / onDemandGpuCount
        : 0,
      onDemandCostPerGib: onDemandRamGib ? onDemandRamCost / onDemandRamGib : 0,
      onDemandCpuCount,
      onDemandUptime,
      onDemandCpuCost,
      onDemandRamCost,
      onDemandRamGib,
      onDemandGpuCount,
      onDemandGpuCost,

      fallbackCost,
      fallbackCostPerCpu: fallbackCpuCount
        ? fallbackCpuCost / fallbackCpuCount
        : 0,
      fallbackCostPerGpu: fallbackGpuCount
        ? fallbackGpuCost / fallbackGpuCount
        : 0,
      fallbackCostPerGib: fallbackRamGib ? fallbackRamCost / fallbackRamGib : 0,
      fallbackCpuCount,
      fallbackUptime,
      fallbackCpuCost,
      fallbackRamCost,
      fallbackRamGib,
      fallbackGpuCount,
      fallbackGpuCost,

      spotCost,
      spotCostPerCpu: spotCpuCount ? spotCpuCost / spotCpuCount : 0,
      spotCostPerGpu: spotGpuCount ? spotGpuCost / spotGpuCount : 0,
      spotCostPerGib: spotRamGib ? spotRamCost / spotRamGib : 0,
      spotCpuCount,
      spotUptime,
      spotCpuCost,
      spotRamCost,
      spotRamGib,
      spotGpuCount,
      spotGpuCost,
    });
  }

  return result;
}

type MapClusterCostDataParams = {
  endTime: DateTime;
  data: CostReport[];
  granularity: DataGranularity;
};

export function mapClusterCostData({
  data,
  endTime,
  granularity,
}: MapClusterCostDataParams): HourlyClusterCostReportData[] {
  if (!data?.length) {
    return [];
  }

  const costReport = mapToClusterCostReport(data);

  const costReportWithForecast = applyMovingAverage({
    data: costReport,
    endTime,
    granularity,
    // window is a number of points to cover 7 days
    windowSize: (7 * SECONDS_IN_DAY) / granularityToSeconds(granularity),
  });

  return costReportWithForecast;
}
